import * as THREE from 'three';
import { Spell, SpellConfig } from './Spell';

/**
 * Blizzard - Ultimate two-handed spell (both hands swipe down).
 * AOE ice storm that drifts toward target, dealing initial damage
 * plus slow and damage-over-time for 4 seconds.
 * Visual: swirling ice storm cloud with snowflakes and frost particles.
 */
export class BlizzardSpell extends Spell {
  private stormCloud!: THREE.Mesh;
  private snowParticles!: THREE.Points;
  private snowPositions!: Float32Array;
  private snowVelocities!: Float32Array;
  private frostRing!: THREE.Mesh;
  private impactAnimationId: number | null = null;

  constructor(
    scene: THREE.Scene,
    startPos: THREE.Vector3,
    targetPos: THREE.Vector3,
    damage: number,
    isPlayerSpell: boolean = true,
  ) {
    const config: SpellConfig = {
      type: 'blizzard',
      damage,
      speed: 8, // Slower drifting storm
      color: new THREE.Color(0x88CCFF),
      particleCount: 100,
      chargeTime: 1.5,
    };
    super(scene, startPos, targetPos, config, isPlayerSpell);
  }

  protected createSpellVisual(): void {
    // Storm cloud - dark bluish mass
    const cloudGeometry = new THREE.SphereGeometry(1.2, 16, 16);
    const cloudMaterial = new THREE.MeshBasicMaterial({
      color: 0x4466AA,
      transparent: true,
      opacity: 0.5,
    });
    this.stormCloud = new THREE.Mesh(cloudGeometry, cloudMaterial);
    this.stormCloud.scale.set(1.5, 0.6, 1.5);
    this.stormCloud.position.y = 0.5;
    this.mesh.add(this.stormCloud);

    // Inner lighter cloud
    const innerCloudGeo = new THREE.SphereGeometry(0.8, 12, 12);
    const innerCloudMat = new THREE.MeshBasicMaterial({
      color: 0x88AADD,
      transparent: true,
      opacity: 0.4,
    });
    const innerCloud = new THREE.Mesh(innerCloudGeo, innerCloudMat);
    innerCloud.scale.set(1.3, 0.5, 1.3);
    innerCloud.position.y = 0.3;
    this.mesh.add(innerCloud);

    // Frost ground ring
    const ringGeo = new THREE.RingGeometry(0.8, 2.0, 24);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xAADDFF,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
    });
    this.frostRing = new THREE.Mesh(ringGeo, ringMat);
    this.frostRing.rotation.x = -Math.PI / 2;
    this.frostRing.position.y = -0.8;
    this.mesh.add(this.frostRing);

    this.createSnowParticles();
  }

  private createSnowParticles(): void {
    const snowCount = 60;
    this.snowPositions = new Float32Array(snowCount * 3);
    this.snowVelocities = new Float32Array(snowCount * 3);

    for (let i = 0; i < snowCount; i++) {
      // Spread snow in a cylinder around the storm
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 1.5;
      this.snowPositions[i * 3] = Math.cos(angle) * radius;
      this.snowPositions[i * 3 + 1] = Math.random() * 2 - 0.5;
      this.snowPositions[i * 3 + 2] = Math.sin(angle) * radius;

      // Downward + swirling velocities
      this.snowVelocities[i * 3] = (Math.random() - 0.5) * 0.5;
      this.snowVelocities[i * 3 + 1] = -(0.5 + Math.random() * 1.5);
      this.snowVelocities[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.snowPositions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xDDEEFF,
      size: 0.12,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
    });

    this.snowParticles = new THREE.Points(geometry, material);
    this.mesh.add(this.snowParticles);
  }

  protected createParticles(): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.config.particleCount * 3);
    const colors = new Float32Array(this.config.particleCount * 3);

    for (let i = 0; i < this.config.particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 2;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = Math.random() * 1.5 - 0.5;
      positions[i * 3 + 2] = Math.sin(angle) * radius;

      // Ice blue to white gradient
      const t = Math.random();
      if (t < 0.3) {
        colors[i * 3] = 0.9; colors[i * 3 + 1] = 0.95; colors[i * 3 + 2] = 1;
      } else {
        colors[i * 3] = 0.5 + Math.random() * 0.2;
        colors[i * 3 + 1] = 0.7 + Math.random() * 0.2;
        colors[i * 3 + 2] = 1;
      }
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.08,
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
    });

    const particles = new THREE.Points(geometry, material);
    this.mesh.add(particles);
    return particles;
  }

  protected updateParticles(delta: number): void {
    // Swirl the main ice particles
    if (this.particles && this.particles.geometry.attributes.position) {
      const positions = this.particles.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < this.config.particleCount; i++) {
        const x = positions[i * 3];
        const z = positions[i * 3 + 2];
        const angle = Math.atan2(z, x) + delta * 3;
        const radius = Math.sqrt(x * x + z * z);
        positions[i * 3] = Math.cos(angle) * radius;
        positions[i * 3 + 2] = Math.sin(angle) * radius;
        positions[i * 3 + 1] += (Math.random() - 0.5) * 0.05;

        // Keep within bounds
        if (Math.abs(positions[i * 3 + 1]) > 1.5) {
          positions[i * 3 + 1] *= 0.8;
        }
      }
      this.particles.geometry.attributes.position.needsUpdate = true;
    }

    // Animate snow falling
    if (this.snowParticles && this.snowPositions && this.snowVelocities) {
      const snowCount = this.snowPositions.length / 3;
      for (let i = 0; i < snowCount; i++) {
        this.snowPositions[i * 3] += this.snowVelocities[i * 3] * delta;
        this.snowPositions[i * 3 + 1] += this.snowVelocities[i * 3 + 1] * delta;
        this.snowPositions[i * 3 + 2] += this.snowVelocities[i * 3 + 2] * delta;

        // Reset snowflakes that fall too far
        if (this.snowPositions[i * 3 + 1] < -1.5) {
          const angle = Math.random() * Math.PI * 2;
          const radius = Math.random() * 1.5;
          this.snowPositions[i * 3] = Math.cos(angle) * radius;
          this.snowPositions[i * 3 + 1] = 1 + Math.random();
          this.snowPositions[i * 3 + 2] = Math.sin(angle) * radius;
        }
      }
      this.snowParticles.geometry.attributes.position.needsUpdate = true;
    }

    // Pulsing storm cloud
    if (this.stormCloud) {
      const s = 1 + Math.sin(Date.now() * 0.004) * 0.08;
      this.stormCloud.scale.set(1.5 * s, 0.6, 1.5 * s);
    }

    // Rotate frost ring
    if (this.frostRing) {
      this.frostRing.rotation.z += delta * 2;
      const ringMat = this.frostRing.material as THREE.MeshBasicMaterial;
      ringMat.opacity = 0.2 + Math.sin(Date.now() * 0.005) * 0.1;
    }
  }

  public createImpactEffect(): void {
    this.emitEvent('impact', { position: this.impactPosition.toArray(), type: 'blizzard' });

    const impactPos = this.impactPosition;
    const burstCount = 200;
    const positions = new Float32Array(burstCount * 3);
    const colors = new Float32Array(burstCount * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < burstCount; i++) {
      positions[i * 3] = impactPos.x;
      positions[i * 3 + 1] = impactPos.y;
      positions[i * 3 + 2] = impactPos.z;

      const t = i / burstCount;
      if (t < 0.3) {
        colors[i * 3] = 0.95; colors[i * 3 + 1] = 0.98; colors[i * 3 + 2] = 1;
      } else if (t < 0.6) {
        colors[i * 3] = 0.5 + Math.random() * 0.2;
        colors[i * 3 + 1] = 0.7 + Math.random() * 0.2;
        colors[i * 3 + 2] = 1;
      } else {
        colors[i * 3] = 0.3 + Math.random() * 0.2;
        colors[i * 3 + 1] = 0.5 + Math.random() * 0.2;
        colors[i * 3 + 2] = 0.9;
      }

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 3 + Math.random() * 8;
      velocities.push(new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.abs(Math.sin(phi) * Math.sin(theta)) * speed * 0.3 + 0.5,
        Math.cos(phi) * speed,
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
    });

    const burst = new THREE.Points(geometry, material);
    this.scene.add(burst);

    // Flash light
    const flashLight = new THREE.PointLight(0x88CCFF, 5, 15);
    flashLight.position.copy(impactPos);
    this.scene.add(flashLight);

    // Ice ground frost
    const frostGeo = new THREE.CircleGeometry(2.5, 32);
    const frostMat = new THREE.MeshBasicMaterial({
      color: 0xAADDFF,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
    });
    const frost = new THREE.Mesh(frostGeo, frostMat);
    frost.position.set(impactPos.x, 0.05, impactPos.z);
    frost.rotation.x = -Math.PI / 2;
    this.scene.add(frost);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = burst.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < burstCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        velocities[i].y -= 3 * 0.016;
      }
      burst.geometry.attributes.position.needsUpdate = true;

      material.opacity = Math.max(0, 1 - (elapsed / 1.0));
      flashLight.intensity = 5 * Math.max(0, 1 - (elapsed / 0.3));
      frostMat.opacity = 0.5 * Math.max(0, 1 - (elapsed / 2.5));

      if (elapsed < 1.2) {
        this.impactAnimationId = requestAnimationFrame(animate);
      } else {
        this.impactAnimationId = null;
        this.scene.remove(burst);
        this.scene.remove(flashLight);
        this.scene.remove(frost);
        geometry.dispose();
        material.dispose();
        flashLight.dispose();
        frostGeo.dispose();
        frostMat.dispose();
      }
    };

    this.impactAnimationId = requestAnimationFrame(animate);
  }

  dispose(): void {
    if (this.impactAnimationId !== null) {
      cancelAnimationFrame(this.impactAnimationId);
      this.impactAnimationId = null;
    }
    super.dispose();
  }
}
